import { createContext, useContext, useState, type ReactNode } from "react"

// Shared Types
export interface User {
  id: string
  name: string
  email: string
  phone?: string
  role: "admin" | "manager" | "operator" | "viewer"
  department?: string
  status: "active" | "inactive" | "invited"
  permissions: string[]
  avatar?: string
  lastActive?: string
  createdAt: string
}

export interface Address {
  id: string
  name: string
  type: "site" | "office" | "warehouse" | "home"
  address: string
  city: string
  region: string
  coordinates?: { lat: number; lng: number }
  contactName?: string
  contactPhone?: string
  isDefault?: boolean
  isFavorite?: boolean
  projectId?: string
}

export interface Project {
  id: string
  name: string
  client: string
  location: string
  addressId?: string
  status: "active" | "completed" | "on-hold" | "planning"
  progress: number
  startDate: string
  totalOrders: number
  deliveredTons: number
  targetTons: number
  contractId: string
  lastDelivery: string
  team: string[]
  managerId?: string
}

export interface Order {
  id: string
  productEn: string
  productAr: string
  quantity: string
  quantityAr: string
  orderDate: string
  orderDateAr: string
  deliveryDate: string
  deliveryDateAr: string
  amount: string
  amountAr: string
  status: "pending" | "processing" | "delivered" | "cancelled"
  projectId?: string
  addressId?: string
  contractId?: string
  createdBy?: string
  assignedDriver?: string
}

export interface Contract {
  id: string
  nameEn: string
  nameAr: string
  type: string
  typeAr: string
  startDate: string
  startDateAr: string
  value: string
  valueAr: string
  status: "active" | "completed" | "draft"
  projectIds?: string[]
  accountManagerId?: string
}

export interface Asset {
  id: string
  name: string
  type: string
  status: "active" | "maintenance" | "idle"
  ownership: "owned" | "leased" | "subcontracted" | "rented"
  plateNumber?: string
  driverId?: string
  currentProjectId?: string
}

interface DataContextType {
  users: User[]
  addresses: Address[]
  projects: Project[]
  orders: Order[]
  contracts: Contract[]
  assets: Asset[]
  currentUser: User | null
  addUser: (user: Omit<User, "id" | "createdAt">) => void
  updateUser: (id: string, updates: Partial<User>) => void
  deleteUser: (id: string) => void
  getProjectOrders: (projectId: string) => Order[]
  getProjectAddresses: (projectId: string) => Address[]
  getContractProjects: (contractId: string) => Project[]
  getUserProjects: (userId: string) => Project[]
  getAddressByProject: (projectId: string) => Address | undefined
}

const DataContext = createContext<DataContextType | undefined>(undefined)

const mockUsers: User[] = [
  {
    id: "USR-001",
    name: "Ahmed Al-Farsi",
    email: "ahmed@alrashid.com",
    phone: "+966 50 123 4567",
    role: "admin",
    department: "Management",
    status: "active",
    permissions: ["all"],
    lastActive: "2024-12-02T10:30:00",
    createdAt: "2023-01-15",
  },
  {
    id: "USR-002",
    name: "Sara Mohammed",
    email: "sara@alrashid.com",
    phone: "+966 55 234 5678",
    role: "manager",
    department: "Procurement",
    status: "active",
    permissions: ["orders.create", "orders.view", "contracts.view", "projects.manage"],
    lastActive: "2024-12-02T09:15:00",
    createdAt: "2023-03-20",
  },
  {
    id: "USR-003",
    name: "Khalid Ibrahim",
    email: "khalid@alrashid.com",
    phone: "+966 54 345 6789",
    role: "operator",
    department: "Operations",
    status: "active",
    permissions: ["orders.create", "orders.view", "shipments.view"],
    lastActive: "2024-12-01T16:45:00",
    createdAt: "2023-06-10",
  },
]

const mockAddresses: Address[] = [
  {
    id: "ADDR-001",
    name: "Al-Riyadh Tower Site",
    type: "site",
    address: "King Fahd Road, Building Zone A",
    city: "Riyadh",
    region: "Riyadh Region",
    contactName: "Mohammed Ali",
    contactPhone: "+966 50 111 2222",
    isDefault: true,
    projectId: "PRJ-001",
  },
  {
    id: "ADDR-002",
    name: "Jeddah Waterfront Site",
    type: "site",
    address: "Corniche Road, Plot 45",
    city: "Jeddah",
    region: "Makkah Region",
    contactName: "Hassan Ahmed",
    contactPhone: "+966 55 333 4444",
    projectId: "PRJ-002",
  },
  {
    id: "ADDR-003",
    name: "Main Office",
    type: "office",
    address: "Industrial Area, Building 12",
    city: "Riyadh",
    region: "Riyadh Region",
    contactName: "Reception",
    contactPhone: "+966 11 456 7890",
    isFavorite: true,
  },
  {
    id: "ADDR-004",
    name: "Dammam Industrial Zone",
    type: "warehouse",
    address: "Second Industrial City, Block 5",
    city: "Dammam",
    region: "Eastern Region",
    contactName: "Faisal Omar",
    contactPhone: "+966 56 444 5555",
    projectId: "PRJ-003",
  },
]

const mockProjects: Project[] = [
  {
    id: "PRJ-001",
    name: "Al-Riyadh Tower",
    client: "Al-Rashid Construction",
    location: "Riyadh",
    addressId: "ADDR-001",
    status: "active",
    progress: 65,
    startDate: "2024-01-15",
    totalOrders: 45,
    deliveredTons: 2800,
    targetTons: 4500,
    contractId: "CNT-001",
    lastDelivery: "2024-12-01",
    team: ["USR-002", "USR-003"],
    managerId: "USR-002",
  },
  {
    id: "PRJ-002",
    name: "Jeddah Waterfront Complex",
    client: "Al-Rashid Construction",
    location: "Jeddah",
    addressId: "ADDR-002",
    status: "active",
    progress: 40,
    startDate: "2024-03-01",
    totalOrders: 28,
    deliveredTons: 1200,
    targetTons: 3000,
    contractId: "CNT-002",
    lastDelivery: "2024-11-28",
    team: ["USR-003"],
    managerId: "USR-001",
  },
  {
    id: "PRJ-003",
    name: "Dammam Logistics Hub",
    client: "Al-Rashid Construction",
    location: "Dammam",
    addressId: "ADDR-004",
    status: "planning",
    progress: 10,
    startDate: "2024-06-01",
    totalOrders: 5,
    deliveredTons: 150,
    targetTons: 5000,
    contractId: "CNT-003",
    lastDelivery: "2024-11-15",
    team: ["USR-002"],
    managerId: "USR-001",
  },
]

const mockOrders: Order[] = [
  {
    id: "ORD-001",
    productEn: "OPC 42.5N Cement",
    productAr: "أسمنت بورتلاندي عادي 42.5N",
    quantity: "500 bags",
    quantityAr: "500 كيس",
    orderDate: "2024-12-01",
    orderDateAr: "1 ديسمبر 2024",
    deliveryDate: "2024-12-05",
    deliveryDateAr: "5 ديسمبر 2024",
    amount: "SAR 12,500",
    amountAr: "12,500 ر.س",
    status: "delivered",
    projectId: "PRJ-001",
    addressId: "ADDR-001",
    contractId: "CNT-001",
    createdBy: "USR-002",
  },
  {
    id: "ORD-002",
    productEn: "SRC Cement",
    productAr: "أسمنت مقاوم للكبريتات",
    quantity: "30 tons",
    quantityAr: "30 طن",
    orderDate: "2024-12-02",
    orderDateAr: "2 ديسمبر 2024",
    deliveryDate: "2024-12-06",
    deliveryDateAr: "6 ديسمبر 2024",
    amount: "SAR 8,400",
    amountAr: "8,400 ر.س",
    status: "processing",
    projectId: "PRJ-002",
    addressId: "ADDR-002",
    contractId: "CNT-002",
    createdBy: "USR-001",
  },
  {
    id: "ORD-003",
    productEn: "PPC Cement",
    productAr: "أسمنت بورتلاندي بوزولاني",
    quantity: "200 bags",
    quantityAr: "200 كيس",
    orderDate: "2024-12-03",
    orderDateAr: "3 ديسمبر 2024",
    deliveryDate: "2024-12-07",
    deliveryDateAr: "7 ديسمبر 2024",
    amount: "SAR 5,200",
    amountAr: "5,200 ر.س",
    status: "processing",
    projectId: "PRJ-001",
    addressId: "ADDR-001",
    contractId: "CNT-001",
    createdBy: "USR-003",
  },
  {
    id: "ORD-004",
    productEn: "OPC 52.5N Cement",
    productAr: "أسمنت بورتلاندي عادي 52.5N",
    quantity: "25 tons",
    quantityAr: "25 طن",
    orderDate: "2024-12-04",
    orderDateAr: "4 ديسمبر 2024",
    deliveryDate: "2024-12-08",
    deliveryDateAr: "8 ديسمبر 2024",
    amount: "SAR 7,500",
    amountAr: "7,500 ر.س",
    status: "pending",
    projectId: "PRJ-003",
    addressId: "ADDR-004",
    contractId: "CNT-003",
    createdBy: "USR-002",
  },
]

const mockContracts: Contract[] = [
  {
    id: "CNT-001",
    nameEn: "Al-Riyadh Tower Supply Contract",
    nameAr: "عقد توريد برج الرياض",
    type: "Annual Supply",
    typeAr: "توريد سنوي",
    startDate: "2024-01-01",
    startDateAr: "1 يناير 2024",
    value: "SAR 2,500,000",
    valueAr: "2,500,000 ر.س",
    status: "active",
    projectIds: ["PRJ-001"],
    accountManagerId: "USR-001",
  },
  {
    id: "CNT-002",
    nameEn: "Jeddah Waterfront Agreement",
    nameAr: "اتفاقية واجهة جدة البحرية",
    type: "Project-Based",
    typeAr: "قائم على المشروع",
    startDate: "2024-03-01",
    startDateAr: "1 مارس 2024",
    value: "SAR 1,800,000",
    valueAr: "1,800,000 ر.س",
    status: "active",
    projectIds: ["PRJ-002"],
    accountManagerId: "USR-002",
  },
  {
    id: "CNT-003",
    nameEn: "Dammam Logistics Contract",
    nameAr: "عقد دمام اللوجستي",
    type: "Framework Agreement",
    typeAr: "اتفاقية إطارية",
    startDate: "2024-06-01",
    startDateAr: "1 يونيو 2024",
    value: "SAR 5,000,000",
    valueAr: "5,000,000 ر.س",
    status: "draft",
    projectIds: ["PRJ-003"],
    accountManagerId: "USR-001",
  },
  {
    id: "CNT-004",
    nameEn: "Spot Purchase Order",
    nameAr: "أمر شراء فوري",
    type: "Spot Order",
    typeAr: "طلب فوري",
    startDate: "2024-11-01",
    startDateAr: "1 نوفمبر 2024",
    value: "SAR 150,000",
    valueAr: "150,000 ر.س",
    status: "completed",
    projectIds: [],
    accountManagerId: "USR-002",
  },
]

const mockAssets: Asset[] = [
  {
    id: "AST-001",
    name: "Cement Mixer Truck A1",
    type: "Mixer Truck",
    status: "active",
    ownership: "owned",
    plateNumber: "RUH-1234",
    driverId: "DRV-001",
    currentProjectId: "PRJ-001",
  },
  {
    id: "AST-002",
    name: "Cement Mixer Truck A2",
    type: "Mixer Truck",
    status: "active",
    ownership: "owned",
    plateNumber: "RUH-1235",
    driverId: "DRV-002",
    currentProjectId: "PRJ-002",
  },
  {
    id: "AST-003",
    name: "Bulk Trailer T1",
    type: "Trailer",
    status: "maintenance",
    ownership: "leased",
    plateNumber: "JED-5678",
  },
  {
    id: "AST-004",
    name: "Delivery Truck B1",
    type: "Delivery Truck",
    status: "active",
    ownership: "subcontracted",
    plateNumber: "DMM-9012",
    driverId: "DRV-003",
    currentProjectId: "PRJ-003",
  },
  {
    id: "AST-005",
    name: "Forklift F1",
    type: "Forklift",
    status: "idle",
    ownership: "rented",
  },
]

export function DataProvider({ children }: { children: ReactNode }) {
  const [users, setUsers] = useState<User[]>(mockUsers)
  const [addresses] = useState<Address[]>(mockAddresses)
  const [projects] = useState<Project[]>(mockProjects)
  const [orders] = useState<Order[]>(mockOrders)
  const [contracts] = useState<Contract[]>(mockContracts)
  const [assets] = useState<Asset[]>(mockAssets)
  const [currentUser] = useState<User | null>(mockUsers[0])

  const addUser = (userData: Omit<User, "id" | "createdAt">) => {
    const newUser: User = {
      ...userData,
      id: `USR-${String(users.length + 1).padStart(3, "0")}`,
      createdAt: new Date().toISOString().split("T")[0],
    }
    setUsers([...users, newUser])
  }

  const updateUser = (id: string, updates: Partial<User>) => {
    setUsers(users.map((u) => (u.id === id ? { ...u, ...updates } : u)))
  }

  const deleteUser = (id: string) => {
    setUsers(users.filter((u) => u.id !== id))
  }

  const getProjectOrders = (projectId: string) => orders.filter((o) => o.projectId === projectId)
  const getProjectAddresses = (projectId: string) => addresses.filter((a) => a.projectId === projectId)
  const getContractProjects = (contractId: string) => projects.filter((p) => p.contractId === contractId)
  const getUserProjects = (userId: string) => projects.filter((p) => p.managerId === userId || p.team.includes(userId))
  const getAddressByProject = (projectId: string) => addresses.find((a) => a.projectId === projectId)

  return (
    <DataContext.Provider
      value={{
        users,
        addresses,
        projects,
        orders,
        contracts,
        assets,
        currentUser,
        addUser,
        updateUser,
        deleteUser,
        getProjectOrders,
        getProjectAddresses,
        getContractProjects,
        getUserProjects,
        getAddressByProject,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}

export function useData() {
  const context = useContext(DataContext)
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider")
  }
  return context
}
